/* eslint-disable sonarjs/no-all-duplicated-branches */
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";

import { Box, Theme } from "@mui/material";
import { styled } from "@mui/system";
import { FormType, PaymentMethod } from "@simplyk/common";
import dynamic from "next/dynamic";

import { Translations } from "../../../../public/@types/translations";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useTranslate } from "../../../hooks/useTranslate";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

const Lottie = dynamic(() => import("react-lottie"), { ssr: false });

export const TIME_BETWEEN_STEPS = 3;
const TIME_FADE = 0.5;

export const getAnimationNumber = ({
  isFree,
  hasADate,
}: { isFree: true; hasADate: boolean } | { isFree: false; hasADate?: boolean }) => {
  return isFree && !hasADate ? 1 : isFree ? 2 : 3;
};

const FadeOutBox = memo(styled("div")`
  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  &.fade-out {
    animation: fadeOut 0.3s ease-in-out forwards;
  }
`);

const FadeBox = memo(styled("div")`
  @keyframes fadeOut {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-40px);
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: translateX(40px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.fade-out {
    animation: fadeOut ${TIME_FADE}s ease-in-out forwards;
  }

  &.fade-in {
    animation: fadeIn ${TIME_FADE}s ease-in-out forwards;
  }
`);

const Dots = memo(styled("div")`
  display: inline-block;
  font-size: 24px;
  font-weight: bold;

  @keyframes dots {
    0%,
    20% {
      content: "\u00A0"; /* Non-breaking space to maintain the height */
    }
    40% {
      content: ".";
    }
    60% {
      content: "..";
    }
    80%,
    100% {
      content: "...";
    }
  }

  &::after {
    content: "\u00A0"; /* Non-breaking space to maintain the height */
    animation: dots 1.5s steps(4, end) infinite;
  }
`);

interface ThankYouLoaderProps {
  organizationName: string;
  formType: FormType;
  generateTaxReceipt: boolean;
  generateTicket: boolean;
  isFree: boolean;
  hasADate: boolean;
  paymentMethod?: PaymentMethod;
  isAuction: boolean;
}

enum STEPS {
  DELIVERY_MONEY = "DELIVERY_MONEY",
  PRINTER = "PRINTER",
  INBOX = "INBOX",
}

const textSx = (theme: Theme) => ({
  color: theme.palette.text.neutral.moderate,
  textAlign: "center",
  marginTop: "-50px",
});

export const ThankYouLoader: FC<ThankYouLoaderProps> = ({
  organizationName,
  formType,
  generateTaxReceipt,
  generateTicket,
  isFree,
  hasADate,
  paymentMethod,
  isAuction,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [step, setStep] = useState(isFree && !hasADate ? STEPS.INBOX : isFree ? STEPS.PRINTER : STEPS.DELIVERY_MONEY);
  const [fade, setFade] = useState("fade-in");
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(true);
  const [thankYouPageAnimationDataDeliveryMoney, setThankYouPageAnimationDataDeliveryMoney] = useState<unknown | null>(
    null
  );
  const [thankYouPageAnimationDataPrinter, setThankYouPageAnimationDataPrinter] = useState<unknown | null>(null);
  const [thankYouPageAnimationDataEmail, setThankYouPageAnimationDataEmail] = useState<unknown | null>(null);
  const paymentMethodIsAchOrPad = paymentMethod === PaymentMethod.Ach || paymentMethod === PaymentMethod.Pad;

  const steps = useMemo(
    () =>
      Object.values(STEPS).filter(
        (value) =>
          !(isFree && value === STEPS.DELIVERY_MONEY) &&
          !(isFree && !hasADate && value === STEPS.PRINTER) &&
          !(paymentMethodIsAchOrPad && value === STEPS.PRINTER) &&
          !(isAuction && value === STEPS.PRINTER)
      ),
    [hasADate, isAuction, isFree, paymentMethodIsAchOrPad]
  );

  useEffect(() => {
    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade("fade-out");

      setTimeout(() => {
        currentIndex = (currentIndex + 1) % steps.length;
        setStep(steps[currentIndex]);
        setFade("fade-in");
      }, TIME_FADE * 1000);
    }, TIME_BETWEEN_STEPS * 1000);

    return () => clearInterval(interval);
  }, [steps]);

  const getPrinterMessageKeyAndAnimationPathName: () => {
    animationType: "printer_calendar" | "printer_receipt" | "printer_tickets";
    messageKey: keyof Translations["common"] & string;
  } = useCallback(() => {
    if (formType === FormType.DonationForm) {
      if (generateTaxReceipt) {
        return {
          animationType: "printer_receipt",
          messageKey: "thankYouLoader.printTaxReceipt",
        };
      } else {
        return {
          animationType: "printer_receipt",
          messageKey: "thankYouLoader.printTransaction",
        };
      }
    } else {
      if (generateTicket) {
        return {
          animationType: "printer_tickets",
          messageKey: "thankYouLoader.printTicket",
        };
      }
      if (generateTaxReceipt) {
        return {
          animationType: "printer_receipt",
          messageKey: "thankYouLoader.printTaxReceipt",
        };
      }
      if (hasADate && isFree) {
        return {
          animationType: "printer_calendar",
          messageKey: "thankYouLoader.printCalendar",
        };
      }
      return {
        animationType: "printer_receipt",
        messageKey: "thankYouLoader.printTransaction",
      };
    }
  }, [formType, generateTaxReceipt, generateTicket, hasADate, isFree]);

  useEffect(() => {
    const fetchThankYouPageAnimation = async () => {
      const printerAnimationType = getPrinterMessageKeyAndAnimationPathName().animationType;
      const [animationDataDeliveryMoney, animationDataPrinter, animationDataEmail] = await Promise.all([
        import("./animations/delivery_money.json"),
        printerAnimationType === "printer_receipt"
          ? import("./animations/printer_receipt.json")
          : printerAnimationType === "printer_tickets"
            ? import("./animations/printer_tickets.json")
            : import("./animations/printer_calendar.json"),
        import("./animations/inbox.json"),
      ]);

      setThankYouPageAnimationDataDeliveryMoney(animationDataDeliveryMoney);
      setThankYouPageAnimationDataPrinter(animationDataPrinter);
      setThankYouPageAnimationDataEmail(animationDataEmail);
      setIsLoadingAnimation(false);
    };
    fetchThankYouPageAnimation();
  }, [getPrinterMessageKeyAndAnimationPathName, organizationName]);

  const getAnimationWithText = useCallback(
    (animationData: unknown, text: string) => {
      return (
        <>
          <Lottie
            options={{
              animationData,
              autoplay: true,
              loop: true,
            }}
            isClickToPauseDisabled={true}
            style={{ width: isSmallScreen ? "100%" : "60%" }}
          />
          <DashboardTypography variant="h5" sx={textSx}>
            {text}
          </DashboardTypography>
          <Box sx={{ textAlign: "center" }}>
            <Dots />
          </Box>
        </>
      );
    },
    [isSmallScreen]
  );

  const animation = useMemo(() => {
    const printerMessageKey = getPrinterMessageKeyAndAnimationPathName().messageKey;
    switch (step) {
      case STEPS.DELIVERY_MONEY:
        return getAnimationWithText(
          thankYouPageAnimationDataDeliveryMoney,
          t(
            "common",
            paymentMethodIsAchOrPad
              ? "thankYouLoader.initiatingTransfer.ach"
              : isAuction
                ? "thankYouLoader.deliveryMoney.auction"
                : formType === FormType.DonationForm
                  ? "thankYouLoader.deliveryMoney.donation"
                  : "thankYouLoader.deliveryMoney.ticketing",
            { orgName: organizationName }
          )
        );
      case STEPS.PRINTER:
        return getAnimationWithText(thankYouPageAnimationDataPrinter, t("common", printerMessageKey));
      case STEPS.INBOX:
        return getAnimationWithText(
          thankYouPageAnimationDataEmail,
          t(
            "common",
            paymentMethodIsAchOrPad
              ? "thankYouLoader.sendingEmail.ach"
              : isAuction
                ? "thankYouLoader.sendingEmail.auction"
                : steps.length === 1
                  ? "thankYouLoader.email.only-one-animation"
                  : "thankYouLoader.email"
          )
        );
    }
  }, [
    getPrinterMessageKeyAndAnimationPathName,
    step,
    getAnimationWithText,
    thankYouPageAnimationDataDeliveryMoney,
    t,
    paymentMethodIsAchOrPad,
    isAuction,
    formType,
    organizationName,
    thankYouPageAnimationDataPrinter,
    thankYouPageAnimationDataEmail,
    steps.length,
  ]);

  return (
    <FadeOutBox sx={{ margin: "auto" }} data-test="thank-you-loader">
      {isLoadingAnimation ? <DashboardZeffyLoader /> : <FadeBox className={fade}>{animation}</FadeBox>}
    </FadeOutBox>
  );
};
